import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import DataTable from 'material-table';
import { Paper } from '@material-ui/core';
import {
  LoadingOverlay,
  makeStyles,
  ThemeProvider,
} from '@knockrentals/knock-shared-web';
import { Toaster } from '@knockrentals/knock-react';
import { useFeatureFlagContext } from '../../../Context/FeatureFlagContext';
import { FilterProperty } from './Components/FilterBoxes';
import { getVirtualAgents, updateVirtualAgent } from '../VirtualAgentAPI';
import { virtualAgentTableOptions } from './table-options';
import { getGeneralSettingsColumns } from './getGeneralSettingsColumns';
import { GENERAL_SETTINGS_FIELDS } from './constants';

const isAiLeasingOnlyField = (field) =>
  field === GENERAL_SETTINGS_FIELDS.CALL_ROUTING ||
  field === GENERAL_SETTINGS_FIELDS.RPCC_AGENT_ROLLOVER ||
  field === GENERAL_SETTINGS_FIELDS.CHAT_ROLLOVER;

const isAiResidentOnlyField = (field) =>
  field === GENERAL_SETTINGS_FIELDS.RESIDENT_CALL_ROUTING;

// Ring Time and Answer Verification are enabled when EITHER Call Routing or
// Resident Call Routing is set to Missed Calls (see getGeneralSettingsColumns),
// so they serve both products and stay visible until both have migrated.
const isSharedAiField = (field) =>
  field === GENERAL_SETTINGS_FIELDS.OFFER_TIMES ||
  field === GENERAL_SETTINGS_FIELDS.ANSWER_VERIFICATION;

const useStyles = makeStyles({
  filterAndEditContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '10px',
    marginTop: '24px'
  }
});

export const API_ERROR = 'Error updating Virtual Agent - General Settings';

const InternalConfigSettings = ({ companyId, properties }) => {
  const classes = useStyles();
  const { isUnifiedLeasingSettingsEnabled, isUnifiedResidentSettingsEnabled } =
    useFeatureFlagContext();
  const [isLoading, setIsLoading] = useState(false);
  const [virtualAgents, setVirtualAgents] = useState([]);
  const [fetchedVirtualAgents, setFetchedVirtualAgents] = useState(false);

  const [filteredVirtualAgents, setFilteredVirtualAgents] = useState([]);
  const [selectedProperties, setSelectedProperties] = useState(
    properties.map(({ propertyId }) => propertyId),
  );
  const filters = {};

  useEffect(() => {
    const getVirtualAgentsData = async () => {
      const virtualAgentsResponse = await getVirtualAgents(companyId, 1, 1000);
      const virtualAgents = (virtualAgentsResponse.results || []).map(
        (virtualAgent) => {
          const property = properties.find(
            (property) => property.propertyId === virtualAgent.property_id,
          );
          return {
            ...virtualAgent,
            leasingEnabled: property.leasingEnabled,
            propertyName: property.propertyName,
            residentEnabled: property.residentEnabled,
          };
        },
      );
      virtualAgents.sort((a, b) =>
        a.propertyName.localeCompare(b.propertyName),
      );
      setVirtualAgents(virtualAgents);
      setFilteredVirtualAgents(virtualAgents);
    };

    if (!fetchedVirtualAgents && properties.length) {
      setIsLoading(true);
      getVirtualAgentsData()
        .catch(() => {
          Toaster.showToast(
            'Error fetching virtual agent data',
            2000,
            Toaster.ToastClasses.error,
          );
        })
        .finally(() => {
          setFetchedVirtualAgents(true);
          setIsLoading(false);
        });
    }
  }, [fetchedVirtualAgents, properties, selectedProperties, companyId]);

  const applyFilters = (currentFilters, currentSelectedProperties) => {
    const filteredVirtualAgents = virtualAgents.filter((virtualAgent) => {
      const isRowValid = Object.keys(currentFilters).every((key) => {
        if (currentFilters[key] === null) {
          return true;
        } else if (key === 'chat_rollover') {
          return (
            (currentFilters[key] && virtualAgent[key] === 'RPCC') ||
            (!currentFilters[key] && virtualAgent[key] === 'NONE')
          );
        } else {
          return virtualAgent[key] === currentFilters[key];
        }
      });

      const isSelected = currentSelectedProperties.length
        ? currentSelectedProperties.includes(virtualAgent.property_id)
        : true;

      return isRowValid && isSelected;
    });

    setFilteredVirtualAgents(filteredVirtualAgents);
  };

  const handlePropertySelect = (properties) => {
    applyFilters(filters, properties);
    setSelectedProperties(properties);
  };

  const saveVaData = async (rowData, field, value) => {
    const payload = {
      field,
      propertyId: rowData.property_id,
      value: field === 'chat_rollover' ? (value ? 'RPCC' : 'NONE') : value,
      virtualAgentId: rowData.id,
    };

    try {
      await updateVirtualAgent(payload);
      Toaster.showToast('Saved!', 2000, Toaster.ToastClasses.success);
      rowData[field] =
        field === 'chat_rollover' ? (value ? 'RPCC' : 'NONE') : value;

      const existingIdx = filteredVirtualAgents.findIndex(
        (va) => va.id === rowData.id,
      );
      setFetchedVirtualAgents([
        ...filteredVirtualAgents.slice(0, existingIdx),
        rowData,
        ...filteredVirtualAgents.slice(
          existingIdx + 1,
          filteredVirtualAgents.length,
        ),
      ]);
    } catch (err) {
      Toaster.showToast(API_ERROR, 2000, Toaster.ToastClasses.error);
    }
  };

  const generalSettingsColumns = getGeneralSettingsColumns(saveVaData).filter(
    (column) =>
      !(isUnifiedLeasingSettingsEnabled && isAiLeasingOnlyField(column.field)) &&
      !(
        isUnifiedResidentSettingsEnabled && isAiResidentOnlyField(column.field)
      ) &&
      !(
        isUnifiedLeasingSettingsEnabled &&
        isUnifiedResidentSettingsEnabled &&
        isSharedAiField(column.field)
      ),
  );

  return (
    <ThemeProvider>
      <div className={classes.filterAndEditContainer}>
        <div style={{ display: 'flex', gap: '10px' }}>
          <FilterProperty
            properties={properties.map((property) => ({
              id: property.propertyId,
              name: property.propertyName,
              selected: selectedProperties.includes(property.propertyId),
            }))}
            onPropertySelect={handlePropertySelect}
          />
        </div>
      </div>
      <LoadingOverlay
        open={isLoading}
        style={{ position: 'absolute', opacity: '0.5' }}
      />
      <DataTable
        className="scheduling-grid-data-table"
        columns={generalSettingsColumns}
        components={{
          Container: (props) => <Paper {...props} variant="outlined" />
        }}
        data={filteredVirtualAgents}
        options={virtualAgentTableOptions}
      />
    </ThemeProvider>
  );
};

InternalConfigSettings.propTypes = {
  companyId: PropTypes.number,
  properties: PropTypes.arrayOf(
    PropTypes.shape({
      aiEmailEnabled: PropTypes.string,
      emailProductEnabled: PropTypes.string,
      genAiEmailEnabled: PropTypes.string,
      leasingEnabled: PropTypes.bool,
      propertyId: PropTypes.number.isRequired,
      propertyName: PropTypes.string.isRequired,
      residentEnabled: PropTypes.bool,
      tableData: PropTypes.shape({
        id: PropTypes.number,
      }),
    }),
  ),
};

export default InternalConfigSettings;
